<template>
    <div class="px-2 py-2">
        <v-data-table-server
            :headers="useHeaders"
            :items="kpiStatistics"
            :loading="isLoading"
            hide-default-footer
            disable-sort
            density="compact"
            color="primary"
            class="elevation-1 table-server"
            data-cy="kpi-statistics-table"
        >
            <template v-slot:item.name="{item}">
                <span class="font-weight-bold"> {{ $t(`paymentKpis.${item.type}`) }} </span>
            </template>
            <template v-slot:item.donePercent="{item}">
                <v-progress-linear v-model="item.donePercent" height="15" :color="getColorProgress(item.donePercent)">
                    <strong>{{ Math.ceil(item.donePercent) }}%</strong>
                </v-progress-linear>
            </template>
            <template v-slot:item.goal="{item}"> {{ $filters.priceFormat(item.goal, item.currencyCode) }} </template>
            <template v-slot:item.sold="{item}">
                {{ $filters.priceFormat(item.sold, item.currencyCode) }}
            </template>
            <template v-slot:item.year="{item, column}">
                <div v-html="getYearHtml(item, column)" />
            </template>
            <template v-slot:item.dueToSell="{item}">
                {{ $filters.priceFormat(item.dueToSell, item.currencyCode) }}
            </template>
        </v-data-table-server>
    </div>
</template>
<script setup lang="ts">
    const props = defineProps<{
        kpiId?: number | null
    }>()

    const isLoading = ref<boolean>(false)
    const kpiStatistics = ref([])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const kpiId = props.kpiId
            kpiStatistics.value = await app.$api.kpiStatistics.get(kpiId)
            kpiStatistics.value.map(kpi => {
                kpi.soldPreviousYears.forEach(item => {
                    kpi[item.year] = item
                })
                return kpi
            })
        } catch (e) {
            kpiStatistics.value = []
        } finally {
            isLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        let headers = [
            {title: '', value: 'name', minWidth: '120px'},
            {title: t('goal'), value: 'goal', align: 'end'},
            {title: `% ${t('done')}`, value: 'donePercent', minWidth: '200px'},
            {title: t('sold'), value: 'sold', align: 'end'},
            {title: t('due_to_sell'), value: 'dueToSell', align: 'end'},
        ]
        if (kpiStatistics.value[0]?.soldPreviousYears?.length) {
            kpiStatistics.value[0].soldPreviousYears.forEach(({year}) => {
                headers.push({title: year, value: 'year', align: 'end'})
            })
        }
        return headers
    })

    const getYearHtml = (item, column) => {
        const year = item[column.title]
        const isPositivePercent = year.percentInCompareToRequestedStartYear >= 0
        const color = isPositivePercent ? 'text-green' : 'text-red'
        return `<div>${app.$filters.priceFormat(year.sold, item.currencyCode)}</div><div class="${color}">(${isPositivePercent ? '+' : '-'}${year.percentInCompareToRequestedStartYear.toFixed(2)}%)</div>`
    }

    const getColorProgress = donePercent => {
        switch (true) {
            case donePercent < 25:
                return 'red'
            case donePercent < 75 && donePercent >= 25:
                return 'orange'
            case donePercent >= 75:
                return 'green'
            default:
                return 'red'
        }
    }

    defineExpose({loadData})
</script>
